import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import styles from './LazyLoad.module.scss';

const DEFAULT_OFFSET_BOTTOM = 350;
const DEFAULT_OFFSET_HORIZONTAL = 3500;

/**
 * Lazy-renders children when the wrapper nears the viewport.
 * Drop-in replacement for react-lazy-load with IntersectionObserver.
 */
const LazyLoad = ({
  children,
  className,
  elementType: Element = 'div',
  height,
  offsetBottom = DEFAULT_OFFSET_BOTTOM,
  offsetHorizontal = DEFAULT_OFFSET_HORIZONTAL,
  offsetLeft,
  offsetRight,
  offsetTop = 0,
  onContentVisible,
  style,
  width,
}) => {
  const [visible, setVisible] = useState(false);
  const nodeRef = useRef(null);

  useEffect(() => {
    if (visible) {
      return undefined;
    }

    const node = nodeRef.current;
    if (!node) {
      return undefined;
    }

    if (typeof IntersectionObserver === 'undefined') {
      setVisible(true);
      if (onContentVisible) {
        onContentVisible();
      }
      return undefined;
    }

    const right = offsetRight != null ? offsetRight : offsetHorizontal;
    const left = offsetLeft != null ? offsetLeft : offsetHorizontal;
    const rootMargin = `${offsetTop}px ${right}px ${offsetBottom}px ${left}px`;

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) {
          return;
        }

        setVisible(true);
        if (onContentVisible) {
          onContentVisible();
        }
        observer.disconnect();
      },
      {
        root: null,
        rootMargin,
        threshold: 0,
      }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [
    visible,
    offsetTop,
    offsetBottom,
    offsetHorizontal,
    offsetLeft,
    offsetRight,
    onContentVisible,
  ]);

  const elStyles = {
    ...(style || {}),
    ...(height != null ? { height } : {}),
    ...(width != null ? { width } : {}),
  };

  const elClasses = [
    'LazyLoad',
    visible ? 'is-visible' : null,
    styles.fade,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <Element ref={nodeRef} className={elClasses} style={elStyles}>
      {visible ? React.Children.only(children) : null}
    </Element>
  );
};

LazyLoad.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
  elementType: PropTypes.string,
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  offsetBottom: PropTypes.number,
  offsetHorizontal: PropTypes.number,
  offsetLeft: PropTypes.number,
  offsetRight: PropTypes.number,
  offsetTop: PropTypes.number,
  onContentVisible: PropTypes.func,
  style: PropTypes.object,
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default LazyLoad;
